import { Config, MiqSizesConversionRules } from "../../types/config/config";

/**
 * Determines the MIQ size conversion rule for a given listing based on configurable rules.
 *
 * This function evaluates a set of rules defined in the application's configuration (`config.featureFlags.miqSizesConversionRules`).
 * Each rule contains a set of conditions that are matched against the properties of the provided `listing` object.
 * If all conditions in a rule are satisfied, the corresponding rule is returned.
 *
 * - If a listing property is an array (e.g., from a multi-select input), only the last value is considered for matching.
 * - If no rules match, or if the configuration is missing or malformed, `null` is returned.
 *
 * @param config - The application configuration object containing feature flags and MIQ size conversion rules.
 * @param listing - The listing object whose properties are evaluated against the rule conditions.
 * @returns The matching MIQ size conversion rule if found; otherwise, `null`.
 */
export const getMiqSizeConversions = (config: Config, listing: any): MiqSizesConversionRules | null => {
    if (!config || !config.featureFlags.miqSizesConversionRules || !Array.isArray(config.featureFlags.miqSizesConversionRules)) {
        return null;
    }

    const rules = config.featureFlags.miqSizesConversionRules;

    for (const rule of rules) {
        const conditions = rule.conditions;
        let match = true;

        for (const key in conditions) {
            if (conditions.hasOwnProperty(key)) {
                const conditionValue = conditions[key];
                const listingValue = listing ? listing[key] : undefined;

                if (Array.isArray(listingValue) && Array.isArray(conditionValue)) {
                    // Get the last value if it's an array.
                    // Multi dropdown inputs add the latest value at the end. 
                    // which means newly added value will always at the end
                    if (!conditionValue.every((val: string) => listingValue.includes(val))) {
                        match = false;
                        break;
                    }
                } else if (listingValue !== conditionValue) {
                    match = false;
                    break;
                }
            }
        }

        if (match) {
            return rule; // Return the rule if all conditions match
        }
    }

    return null;
}